/**
 * The saved-address book. Every call requires a signed-in customer.
 *
 * Read by the checkout's address step and written by the account page, which is
 * the only place a customer can correct a typo or delete an address they no
 * longer use. Before that page existed the book was append-only.
 */
import { api } from './client';
import { queryKeys, useApiQuery } from './queryClient';
import type { AddressCreate, AddressListOut, AddressOut, AddressUpdate } from './types';

export const ADDRESSES_PATH = '/api/v1/addresses';

export function fetchAddresses(signal?: AbortSignal): Promise<AddressListOut> {
  return api.get<AddressListOut>(ADDRESSES_PATH, signal ? { signal } : undefined);
}

export function createAddress(body: AddressCreate): Promise<AddressOut> {
  return api.post<AddressOut>(ADDRESSES_PATH, body);
}

/** PATCH: a partial update. The server rejects an empty patch. */
export function updateAddress(id: string, body: AddressUpdate): Promise<AddressOut> {
  return api.patch<AddressOut>(`${ADDRESSES_PATH}/${id}`, body);
}

export function deleteAddress(id: string): Promise<void> {
  return api.delete<void>(`${ADDRESSES_PATH}/${id}`);
}

/**
 * Its own endpoint rather than a PATCH of ``is_default``, because promoting one
 * address demotes another: it is a change to the whole book, not to one row.
 */
export function setDefaultAddress(id: string): Promise<AddressOut> {
  return api.post<AddressOut>(`${ADDRESSES_PATH}/${id}/default`, {});
}

/**
 * The saved-address picker's data.
 *
 * `enabled` is driven by the caller's auth state rather than by catching the
 * 401: a guaranteed-401 request on every anonymous page load is noise in the
 * server's logs and a rate-limit bucket spent for nothing.
 */
export function useAddresses(enabled: boolean) {
  return useApiQuery<AddressListOut, typeof queryKeys.addresses>({
    queryKey: queryKeys.addresses,
    queryFn: ({ signal }) => fetchAddresses(signal),
    enabled,
    staleTime: 60_000,
  });
}
